import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { getProfile, authMe, exchangeSession } from "./lib/api";
import BottomTabs from "./components/BottomTabs";
import InstallPrompt from "./components/InstallPrompt";
import OnboardingPage from "./pages/Onboarding";
import HomePage from "./pages/Home";
import CyclePage from "./pages/Cycle";
import WellnessPage from "./pages/Wellness";
import CoachPage from "./pages/Coach";
import ProfilePage from "./pages/Profile";
import PregnancyPage from "./pages/Pregnancy";
import LegalPage from "./pages/Legal";
import LoginPage from "./pages/Login";
import LibraryPage from "./pages/Library";
import CarePage from "./pages/Care";
import CommunityPage from "./pages/Community";
import ResetPasswordPage from "./pages/ResetPassword";
import { GenesisProvider } from "./genesis";
import "./App.css";

/* AuthCallback \u2014 processes #session_id=... from Emergent Auth, exchanges for cookie,
   then redirects to home. Uses useRef to dedupe under StrictMode. */
function AuthCallback({ onAuthed }) {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    (async () => {
      try {
        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) {
          navigate("/login", { replace: true });
          return;
        }
        const session_id = decodeURIComponent(m[1]);
        await exchangeSession(session_id);
        // Clear the fragment + go home
        window.history.replaceState(null, "", "/");
        if (onAuthed) await onAuthed();
        navigate("/", { replace: true });
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, onAuthed]);

  return (
    <div className="app-shell flex items-center justify-center">
      <div className="phone-frame flex items-center justify-center">
        <div className="flex flex-col items-center gap-3" data-testid="auth-callback-loading">
          <div className="w-14 h-14 rounded-full surface-plum plush-shadow" />
          <p className="text-sm text-muted-foreground font-editorial-italic text-lg">Signing you in…</p>
        </div>
      </div>
    </div>
  );
}

function Shell() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Sync session handling: if the URL has #session_id=, render AuthCallback FIRST.
  const isAuthCallback = location.hash?.includes("session_id=");

  const refresh = async () => {
    try {
      const p = await getProfile();
      setProfile(p);
    } catch {}
  };

  const checkAuth = async () => {
    try {
      const me = await authMe();
      setUser(me);
      try {
        const p = await getProfile();
        setProfile(p);
        if (!p.onboarded && location.pathname !== "/onboarding") {
          navigate("/onboarding", { replace: true });
        }
      } catch {}
    } catch {
      setUser(null);
      if (location.pathname !== "/login") {
        navigate("/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthCallback) {
      setLoading(false);
      return;
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render auth callback BEFORE anything else if hash has session_id
  if (isAuthCallback) {
    return <AuthCallback onAuthed={checkAuth} />;
  }

  if (loading) {
    return (
      <div className="app-shell flex items-center justify-center">
        <div className="phone-frame flex items-center justify-center">
          <div className="flex flex-col items-center gap-3" data-testid="app-loading">
            <div className="w-14 h-14 rounded-full surface-plum plush-shadow" />
            <p className="text-sm text-muted-foreground font-editorial-italic text-lg">Knya</p>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated → only render Login + public routes
  if (!user) {
    return (
      <div className="app-shell">
        <div className="phone-frame">
          <Routes>
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="*" element={<LoginPage />} />
          </Routes>
        </div>
        <Toaster position="top-center" richColors closeButton />
      </div>
    );
  }

  const isOnboarding = location.pathname === "/onboarding";

  return (
    <div className="app-shell">
      <div className="phone-frame">
        <Routes>
          <Route path="/onboarding" element={<OnboardingPage onDone={async () => { await refresh(); navigate("/"); }} />} />
          <Route path="/" element={<HomePage profile={profile} user={user} />} />
          <Route path="/cycle" element={<CyclePage profile={profile} />} />
          <Route path="/pregnancy" element={<PregnancyPage profile={profile} />} />
          <Route path="/wellness" element={<WellnessPage profile={profile} />} />
          <Route path="/coach" element={<CoachPage profile={profile} />} />
          <Route path="/profile" element={<ProfilePage profile={profile} user={user} onProfileUpdate={refresh} onLoggedOut={() => { setUser(null); navigate("/login"); }} />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/library/:articleId" element={<LibraryPage />} />
          <Route path="/care" element={<CarePage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/community/:postId" element={<CommunityPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/login" element={<LoginPage />} />
        </Routes>
        {!isOnboarding && <BottomTabs />}
        {!isOnboarding && <InstallPrompt />}
      </div>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <GenesisProvider>
        <Shell />
      </GenesisProvider>
    </BrowserRouter>
  );
}
